import React, { useState, useMemo, useEffect } from "react";
import {
  Button,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  Icon,
} from "semantic-ui-react";
import { DateTextBox } from "react-lib/apps/common";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import {
  RunSequence,
  SetProp,
  PaySlipType,
  BTN_ACTS,
  CARD_NAME,
} from "./sequence/SelfServiceSchedule";
import SnackMessage from "react-lib/apps/common/SnackMessage";

type CardSelfServicePaySlipProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  buttonLoadCheck?: any;
  errorMessage?: any;
  successMessage?: any;
  runSequence: RunSequence;
  PaySlipSequence?: PaySlipType;
};

function CardSelfServicePaySlip(props: CardSelfServicePaySlipProps) {
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const [activePage, setActivePage] = useState(1);
  const rowsPerPage = 15;

  const onSearchClick = () => {
    if (!fromDate || !toDate) return;

    setActivePage(1);
    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: "PaySlip",
      action: "FETCH",
      filter: {
        start_date: convertToBuddhistYear(fromDate),
        end_date: convertToBuddhistYear(toDate),
      },
    });
  };

  const onFileClick = (employeePayroll: any, language: any) => {
    props.runSequence({
      sequence: "SelfServiceSchedule",
      menu: "PaySlip",
      action: "DETAIL",
      employeePayroll: employeePayroll,
      language: language,
    });
  };

  const handleSnackClose = () => {
    props.setProp(`errorMessage.${CARD_NAME}`, null);
    props.setProp(`successMessage.${CARD_NAME}`, null);
  };

  const paySlipList = useMemo(() => {
    return props.PaySlipSequence?.paySlipList ?? [];
  }, [props.PaySlipSequence?.paySlipList]);

  const paginatedList = useMemo(() => {
    const startIdx = (activePage - 1) * rowsPerPage;
    return paySlipList.slice(startIdx, startIdx + rowsPerPage);
  }, [paySlipList, activePage]);

  const formatDateDisplay = (date: Date) =>
    date
      .toLocaleDateString("en-GB")
      .split("/")
      .map((part) => part.padStart(2, "0"))
      .join("/");

  const convertToBuddhistYear = (dateStr: string) => {
    const [dd, mm, yyyy] = dateStr.split("/").map(Number);
    return `${dd.toString().padStart(2, "0")}/${mm.toString().padStart(2, "0")}/${(
      yyyy + 543
    ).toString()}`;
  };

  const toGregorian = (dateStr: string) => {
    const [dd, mm, yyyy] = dateStr.split("/").map(Number);
    const adYear = yyyy > 2400 ? yyyy - 543 : yyyy;
    return `${dd.toString().padStart(2, "0")}/${mm.toString().padStart(2, "0")}/${adYear}`;
  };

  const formatPaySlipFileName = (date: string) => {
    const [day, month, year] = date.split("/");

    const monthMap: { [key: string]: string } = {
      "01": "Jan",
      "02": "Feb",
      "03": "Mar",
      "04": "Apr",
      "05": "May",
      "06": "Jun",
      "07": "Jul",
      "08": "Aug",
      "09": "Sep",
      "10": "Oct",
      "11": "Nov",
      "12": "Dec",
    };

    const monthName = monthMap[month];

    return `${year}_${monthName}_${day}.pdf`;
  };

  useEffect(() => {
    const today = new Date();
    const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
    const lastDayOfYear = new Date(today.getFullYear(), 11, 31);

    setFromDate(formatDateDisplay(firstDayOfYear));
    setToDate(formatDateDisplay(lastDayOfYear));
  }, []);

  useEffect(() => {
    setActivePage(1);
  }, [paySlipList]);

  const totalPages = Math.ceil(paySlipList.length / rowsPerPage) || 1;

  console.log("Current paySlipList:", paySlipList);

  return (
    <div style={{ height: "calc(100vh - 100px)", overflowY: "auto", padding: "20px" }}>
      <div style={{ fontSize: "20px" }}>Pay Slip</div>
      <hr />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: 15,
          gap: 20,
        }}
      >
        <div style={{ minWidth: "150px", display: "flex", flexDirection: "column" }}>
          <label style={{ fontWeight: "bold", marginBottom: 4 }}>From Date</label>
          <DateTextBox
            placeholder="dd/mm/yyyy"
            inputStyle={{ width: "200px" }}
            value={fromDate}
            onChange={(date: string) => setFromDate(toGregorian(date))}
          />
        </div>

        <div style={{ minWidth: "150px", display: "flex", flexDirection: "column" }}>
          <label style={{ fontWeight: "bold", marginBottom: 4 }}>To Date</label>
          <DateTextBox
            placeholder="dd/mm/yyyy"
            inputStyle={{ width: "200px" }}
            value={toDate}
            onChange={(date: string) => setToDate(toGregorian(date))}
          />
        </div>

        <div style={{ paddingBottom: 4 }}>
          <ButtonLoadCheck
            color="blue"
            onClick={onSearchClick}
            setProp={props.setProp}
            fluid={false}
            size="medium"
            paramKey={BTN_ACTS.PaySlip.FETCH}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.PaySlip.FETCH]}
            title="Search"
            style={{ height: 35, marginTop: 22, width: "200px" }}
          />
        </div>
      </div>
      <SnackMessage
        onEvent={props.onEvent}
        onClose={handleSnackClose}
        error={props.errorMessage?.[CARD_NAME]}
        success={props.successMessage?.[CARD_NAME]}
      />

      <Table celled compact structured style={{ width: '640px' }}>
        <TableHeader>
          <TableRow>
            {[
              "Payment Date",
              "Download Bilingual Pay slip",
            ].map((header) => (
              <TableHeaderCell
                key={header}
                style={{
                  backgroundColor: "#90d5ff",
                  color: "#2c3e50",
                  fontWeight: "bold",
                }}
                textAlign="center"
              >
                {header === "Download Bilingual Pay slip" ? (
                  <>
                    <i className="download icon" style={{ marginRight: 5 }} />
                    {header}
                  </>
                ) : (
                  header
                )}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {[...paginatedList, ...Array(rowsPerPage - paginatedList.length).fill(null)].map(
            (ps, index) => (
              <TableRow key={ps?.id ?? `empty-${index}`}>
                {ps ? (
                  <>
                    <TableCell textAlign="center" style={{ border: "1px solid #ccc" }}>
                      {ps.payment_date ?? "-"}
                    </TableCell>
                    <TableCell textAlign="center" style={{ border: "1px solid #ccc" }}>
                      <label
                        onClick={
                          props.buttonLoadCheck?.[`${ps.id}_TH`] !== "LOADING"
                            ? () => onFileClick(ps.id, "TH")
                            : undefined
                        }
                        style={{
                          cursor:
                            props.buttonLoadCheck?.[`${ps.id}_TH`] === "LOADING"
                              ? "default"
                              : "pointer",
                          color:
                            props.buttonLoadCheck?.[`${ps.id}_TH`] === "LOADING" ? "gray" : "blue",
                          textDecoration:
                            props.buttonLoadCheck?.[`${ps.id}_TH`] === "LOADING"
                              ? "none"
                              : "underline",
                          pointerEvents:
                            props.buttonLoadCheck?.[`${ps.id}_TH`] === "LOADING" ? "none" : "auto",
                        }}
                      >
                        {formatPaySlipFileName(ps.payment_date) || 'Pay Slip'}
                      </label>
                    </TableCell>
                  </>
                ) : (
                  <>
                    {Array.from({ length: 2 }).map((_, colIdx) => (
                      <TableCell
                        key={`empty-cell-${index}-${colIdx}`}
                        style={{ border: "1px solid #ccc", height: "35px" }}
                      >
                        &nbsp;
                      </TableCell>
                    ))}
                  </>
                )}
              </TableRow>
            )
          )}
        </TableBody>
        <Table.Footer>
          <TableRow
            style={{
              backgroundColor: "#f2f2f2",
              height: 40,
            }}
          >
            <TableCell colSpan={8} textAlign="center">
              <Button
                size="mini"
                disabled={activePage === 1}
                onClick={() => setActivePage((prev) => Math.max(prev - 1, 1))}
                style={{ marginRight: 8 }}
              >
                Previous
              </Button>

              <span style={{ fontWeight: "bold", margin: "0 12px" }}>
                {activePage} / {totalPages}
              </span>

              <Button
                size="mini"
                disabled={activePage === totalPages}
                onClick={() => setActivePage((prev) => Math.min(prev + 1, totalPages))}
                style={{ marginLeft: 8 }}
              >
                Next
              </Button>
            </TableCell>
          </TableRow>
        </Table.Footer>
      </Table>
    </div>
  );
}

export default CardSelfServicePaySlip;
